import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { config } from '@/lib/config';

export type Role = 'viewer' | 'operator' | 'admin';

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
  tenantId: string;
  roles: Role[];
  isPlatformAdmin: boolean;
}

type Status = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  user: AuthUser | null;
  status: Status;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  getToken: () => Promise<string | null>;
  hasRole: (role: Role) => boolean;
}

const AuthCtx = createContext<AuthState | null>(null);

const ROLE_HIERARCHY: Record<Role, number> = { viewer: 1, operator: 2, admin: 3 };
const GROUP_PATTERN = /^([a-z0-9-]+):(viewer|operator|admin)$/;
const PLATFORM_ADMIN_GROUP = 'pulse:platform-admin';

const pool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.userPoolClientId,
});

/**
 * Build an AuthUser from a Cognito ID-token session. Group → tenant/role
 * mapping mirrors the backend's extractAuth (packages/pulse-api/src/auth.ts):
 * groups follow `<tenantId>:<role>`, plus the cross-tenant
 * `pulse:platform-admin` group.
 */
function userFromSession(session: CognitoUserSession): AuthUser | null {
  const payload = session.getIdToken().decodePayload();
  const sub = typeof payload['sub'] === 'string' ? payload['sub'] : '';
  if (!sub) return null;

  const rawGroups = payload['cognito:groups'];
  const groups: string[] = Array.isArray(rawGroups)
    ? rawGroups.filter((g): g is string => typeof g === 'string')
    : [];

  const isPlatformAdmin = groups.includes(PLATFORM_ADMIN_GROUP);
  const matches = groups
    .map((g) => GROUP_PATTERN.exec(g))
    .filter((m): m is RegExpExecArray => m !== null);

  const primary = matches[0];
  const tenantId = primary ? (primary[1] as string) : '';
  const roles = Array.from(
    new Set(matches.filter((m) => m[1] === tenantId).map((m) => m[2] as Role)),
  );

  return {
    sub,
    email: typeof payload['email'] === 'string' ? payload['email'] : '',
    name: typeof payload['name'] === 'string' ? payload['name'] : (payload['email'] as string) ?? sub,
    tenantId,
    roles,
    isPlatformAdmin,
  };
}

/** Resolve the current cached session (auto-refreshes via the refresh token if expired). */
function getCurrentSession(): Promise<CognitoUserSession | null> {
  return new Promise((resolve) => {
    const current = pool.getCurrentUser();
    if (!current) return resolve(null);
    current.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(session);
    });
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  // Restore an existing session on first mount.
  useEffect(() => {
    let cancelled = false;
    void getCurrentSession().then((session) => {
      if (cancelled) return;
      const restored = session ? userFromSession(session) : null;
      setUser(restored);
      setStatus(restored ? 'authenticated' : 'unauthenticated');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback((email: string, password: string) => {
    return new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
      const details = new AuthenticationDetails({ Username: email, Password: password });
      cognitoUser.authenticateUser(details, {
        onSuccess: (session) => {
          const next = userFromSession(session);
          if (!next) {
            reject(new Error('Your account has no tenant role assigned. Contact an administrator.'));
            return;
          }
          setUser(next);
          setStatus('authenticated');
          resolve();
        },
        onFailure: (err) => {
          reject(new Error(err?.message ?? 'Sign-in failed.'));
        },
        newPasswordRequired: () => {
          reject(
            new Error(
              'A permanent password must be set for this account before signing in here.',
            ),
          );
        },
      });
    });
  }, []);

  const signOut = useCallback(() => {
    pool.getCurrentUser()?.signOut();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const getToken = useCallback(async () => {
    const session = await getCurrentSession();
    return session ? session.getIdToken().getJwtToken() : null;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      status,
      signIn,
      signOut,
      getToken,
      hasRole: (role) => {
        if (!user) return false;
        if (user.isPlatformAdmin) return true;
        const userMax = Math.max(...user.roles.map((r) => ROLE_HIERARCHY[r]), 0);
        return userMax >= ROLE_HIERARCHY[role];
      },
    }),
    [user, status, signIn, signOut, getToken],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
